import { SVGProps, useMemo } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { withAsyncBoundary } from '@toss/async-boundary';
import { ImpressionArea } from '@toss/impression-area';
import { useTranslation } from 'next-i18next';
import { BoltFillIcon, PlusIcon } from '@zep/icons';
import { SpacePlanDto } from '@zep/types';
import { Button, Dialog, DialogContent, DialogHeader, DialogOverlay, DialogPanel, DialogTitle, Spacing } from '@zep/ui';
import { cn } from '@zep/utils';
import { CreateSpaceFunnel } from '@zep/web-components';
import { PLAN_TYPE_STYLES, SpaceUserRole } from '../../../../../consts';
import { useInfiniteMyRoleSpaces } from '../../../../../services';
import { Space } from '../../../../../types/SpaceTypes';
import { SpaceThumbnail } from '../../../../common/SpaceThumbnail';
import S from './SelectMySpacesModal.module.scss';
const SelectMySpacesModalInner = (props: SelectMySpacesModalProps) => {
  const {
    visible,
    hide,
    remove
  } = useModal();
  const {
    t
  } = useTranslation(['subscription']);
  return <Dialog maxWidth="980px" maxHeight="650px" height="100%" open={visible} onClose={() => null} afterClose={remove} drawer data-sentry-element="Dialog" data-sentry-component="SelectMySpacesModalInner" data-sentry-source-file="SelectMySpacesModal.tsx">
      <DialogOverlay data-sentry-element="DialogOverlay" data-sentry-source-file="SelectMySpacesModal.tsx" />
      <DialogPanel data-sentry-element="DialogPanel" data-sentry-source-file="SelectMySpacesModal.tsx">
        <DialogHeader onClose={hide} data-sentry-element="DialogHeader" data-sentry-source-file="SelectMySpacesModal.tsx">
          <div className={S.header}>
            <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="SelectMySpacesModal.tsx">
              {t('subscription:pricing.modalSpaceSelect.title')}
            </DialogTitle>
            <p className={S.description}>
              {t('subscription:pricing.modalSpaceSelect.description')}
            </p>
          </div>
        </DialogHeader>
        <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="SelectMySpacesModal.tsx">
          <MySpacesModalContent key={'1'} {...props} data-sentry-element="MySpacesModalContent" data-sentry-source-file="SelectMySpacesModal.tsx" />
        </DialogContent>
      </DialogPanel>
    </Dialog>;
};
const Content = (props: SelectMySpacesModalProps) => {
  const myRoleSpaces = useInfiniteMyRoleSpaces(['OWNER', 'ADMIN']);
  const relatedSpaces = useMemo(() => myRoleSpaces.data ? myRoleSpaces.data.pages.flatMap(data => data.spaces) : [], [myRoleSpaces.data]);
  const fetchNextData = async () => {
    if (myRoleSpaces.hasNextPage && !myRoleSpaces.isFetchingNextPage) {
      await myRoleSpaces.fetchNextPage();
    }
  };
  if (relatedSpaces.length === 0) {
    return <EmptySpace />;
  }
  return <div className={S.list} role="list" data-sentry-component="Content" data-sentry-source-file="SelectMySpacesModal.tsx">
      {relatedSpaces.map((space, index) => <SpaceCard key={space.hashId} space={space} spacePlan={space.spacePlan} isLazy={relatedSpaces.length === index + 1} onSelectSpace={props.onSelectSpace} />)}
      <ImpressionArea onImpressionStart={fetchNextData} areaThreshold={0.5} data-sentry-element="ImpressionArea" data-sentry-source-file="SelectMySpacesModal.tsx">
        <div />
      </ImpressionArea>
    </div>;
};
const SpaceCard = ({
  space,
  spacePlan,
  onSelectSpace,
  isLazy
}: {
  space: Space;
  spacePlan: SpacePlanDto;
  isLazy: boolean;
} & SelectMySpacesModalProps) => {
  return <div key={space.hashId} className={S.item} role="listitem" onClick={() => onSelectSpace(space)} data-sentry-component="SpaceCard" data-sentry-source-file="SelectMySpacesModal.tsx">
      <SpaceThumbnail key={space.hashId} badge={space.myRole === SpaceUserRole.OWNER ? 'owner' : 'admin'} src={space.thumbnailUrl} alt="thumbnail" isLazy={isLazy} data-sentry-element="SpaceThumbnail" data-sentry-source-file="SelectMySpacesModal.tsx" />
      <div className="flex flex-col justify-start gap-[4px]">
        <span className="text-subtitle font-[500] text-gray-900">
          {space.name}
        </span>
        {spacePlan?.status === 'ACTIVE' && <PlanNameChip spacePlan={spacePlan} />}
      </div>
    </div>;
};
const PlanNameChip = ({
  spacePlan
}: {
  spacePlan: SpacePlanDto;
}) => {
  const {
    bg,
    text,
    fill
  } = PLAN_TYPE_STYLES[spacePlan.planType] || {};
  return <div className={cn('flex w-fit items-center gap-[2px] rounded-[20px] py-[2px] pl-[5px] pr-[6px]', bg)} data-sentry-component="PlanNameChip" data-sentry-source-file="SelectMySpacesModal.tsx">
      <BoltFillIcon width={12} height={12} fill={fill} data-sentry-element="BoltFillIcon" data-sentry-source-file="SelectMySpacesModal.tsx" />
      <span className={cn('text-body-2 font-[600]', text)}>
        {spacePlan.planType}
      </span>
    </div>;
};
const EmptySpace = () => {
  const {
    t
  } = useTranslation(['subscription', 'home']);
  const createSpaceFunnel = useModal(CreateSpaceFunnel);
  return <>
      <Spacing size={130} data-sentry-element="Spacing" data-sentry-source-file="SelectMySpacesModal.tsx" />
      <div className="flex w-full flex-col items-center justify-start bg-white">
        <EmptyStateSvg data-sentry-element="EmptyStateSvg" data-sentry-source-file="SelectMySpacesModal.tsx" />
        <Spacing size={18} data-sentry-element="Spacing" data-sentry-source-file="SelectMySpacesModal.tsx" />
        <span className="text-h5 font-[700] text-gray-900">
          {t('subscription:pricing.alert.titleDeletedSpace')}
        </span>
        <Spacing size={8} data-sentry-element="Spacing" data-sentry-source-file="SelectMySpacesModal.tsx" />
        <span className="text-body-1 font-[500] text-gray-600">
          {t('subscription:pricing.selectSpaceModal.description')}
        </span>
        <Spacing size={18} data-sentry-element="Spacing" data-sentry-source-file="SelectMySpacesModal.tsx" />
        <Button size="md" onClick={async () => {
        await createSpaceFunnel.show({
          from: 'home'
        });
      }} leftSlot={<PlusIcon width={16} height={16} />} data-sentry-element="Button" data-sentry-source-file="SelectMySpacesModal.tsx">
          {t('home:space_me_create_space_button')}
        </Button>
      </div>
    </>;
};
const MySpacesModalContent = withAsyncBoundary(Content, {
  onError: () => {},
  pendingFallback: null,
  rejectedFallback: () => {
    return null;
  }
});
export const SelectMySpacesModal = NiceModal.create(SelectMySpacesModalInner);
type SelectMySpacesModalProps = {
  onSelectSpace: (space: Space) => void;
  existSpaceHashId?: string;
};
const EmptyStateSvg = (props: SVGProps<SVGSVGElement>) => <svg xmlns="http://www.w3.org/2000/svg" width={88} height={60} fill="none" {...props} data-sentry-element="svg" data-sentry-component="EmptyStateSvg" data-sentry-source-file="SelectMySpacesModal.tsx">
    <path fill="#fff" d="M25.765 0C11.898 0 .656 11.149.656 24.901c0 13.753 11.242 24.901 25.109 24.901h12.45l5.518 9.464a.69.69 0 0 0 1.19 0l5.517-9.464h12.45C76.757 49.802 88 38.654 88 24.902 87.999 11.148 76.757 0 62.89 0H25.765Z" data-sentry-element="path" data-sentry-source-file="SelectMySpacesModal.tsx" />
    <path fill="#D5D9E0" fillRule="evenodd" d="m39.802 47.075 4.526 7.763 4.526-7.763H62.89c12.349 0 22.359-9.928 22.359-22.174 0-12.246-10.01-22.174-22.359-22.174H25.765c-12.348 0-22.359 9.928-22.359 22.174 0 12.246 10.01 22.174 22.359 22.174h14.037Zm5.12 12.191a.69.69 0 0 1-1.19 0l-5.517-9.464h-12.45c-13.867 0-25.109-11.148-25.109-24.9C.656 11.148 11.898 0 25.765 0H62.89C76.757 0 88 11.149 88 24.901c0 13.753-11.242 24.901-25.109 24.901H50.44l-5.517 9.464Z" clipRule="evenodd" data-sentry-element="path" data-sentry-source-file="SelectMySpacesModal.tsx" />
    <ellipse cx={28.66} cy={25.674} fill="#5E6066" rx={2.647} ry={2.621} data-sentry-element="ellipse" data-sentry-source-file="SelectMySpacesModal.tsx" />
    <ellipse cx={44.539} cy={25.674} fill="#5E6066" rx={2.647} ry={2.621} data-sentry-element="ellipse" data-sentry-source-file="SelectMySpacesModal.tsx" />
    <ellipse cx={60.418} cy={25.674} fill="#5E6066" rx={2.647} ry={2.621} data-sentry-element="ellipse" data-sentry-source-file="SelectMySpacesModal.tsx" />
  </svg>;